import * as React from 'react';

import { Button } from '@cotera/client/v0/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { cn } from '@cotera/client/v0/lib/utils';
import { Icon } from '../../../../ui/icon/icon';
import type { CommentReply } from '../../../../ui/tiptap/extensions/comment-mark';
import { CommentByline } from './comment-byline';

/**
 * One message, open for rewriting.
 *
 * Confirm and cancel are icons at the foot of the box rather than worded buttons,
 * because the box is already sitting inside a card that has its own controls — two
 * more labels there would read as belonging to the thread rather than to the sentence
 * being changed.
 */
function CommentMessageEditor({
  initialBody,
  onSave,
  onCancel,
}: {
  initialBody: string;
  onSave: (body: string) => void;
  onCancel: () => void;
}) {
  const [body, setBody] = React.useState(initialBody);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const node = textareaRef.current;
    if (node === null) {
      return;
    }
    node.focus();
    // Caret at the end, not over the whole message: editing is nearly always a change
    // to something already written, and a full selection means the first keystroke
    // destroys it.
    node.setSelectionRange(node.value.length, node.value.length);
  }, []);

  const save = () => {
    const trimmed = body.trim();
    if (trimmed === '' || trimmed === initialBody.trim()) {
      onCancel();
      return;
    }
    onSave(trimmed);
  };

  return (
    <div className="mt-1 rounded-lg border border-border bg-background p-2">
      <textarea
        ref={textareaRef}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            if (event.nativeEvent.isComposing) {
              return;
            }
            event.preventDefault();
            save();
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            onCancel();
          }
        }}
        rows={3}
        className="w-full resize-none bg-transparent text-sm leading-5 outline-none"
      />
      <div className="flex items-center justify-end gap-1 pt-1">
        {/* A filled circle like the tick beside it, in the muted tone rather than
            the accent: the pair reads as one choice with two answers, and the one
            that keeps your edit should still be the louder of the two. */}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label="Cancel edit"
          className="size-7 rounded-full p-0"
          onClick={onCancel}
        >
          <Icon icon="x" className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          aria-label="Save edit"
          className="size-7 rounded-full p-0"
          disabled={body.trim() === ''}
          onClick={save}
        >
          <Icon icon="check" className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Edit and delete for one message.
 *
 * Its own component because it has two homes: on the message, where it belongs, and —
 * for the message that opens a thread — up in the card's corner beside the resolve
 * control. Those two used to be absolutely positioned inside different boxes, which
 * made "which is on the left" a question about padding rather than about markup.
 */
export function CommentMessageMenu({
  reply,
  onStartEdit,
  onDelete,
  className,
}: {
  reply: CommentReply;
  onStartEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}) {
  if (onStartEdit === undefined && onDelete === undefined) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Actions for ${reply.authorName}'s comment`}
          className={cn('size-7 bg-popover p-0', className)}
        >
          <Icon icon="ellipsis-horizontal" className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom">
        {onStartEdit !== undefined ? (
          <DropdownMenuItem onSelect={onStartEdit}>
            <Icon icon="pen-line" />
            Edit
          </DropdownMenuItem>
        ) : null}
        {onDelete !== undefined ? (
          <DropdownMenuItem variant="destructive" onSelect={onDelete}>
            <Icon icon="trash" />
            Delete
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CommentMessage({
  reply,
  quote,
  editing,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onDelete,
  hostsOwnMenu,
}: {
  reply: CommentReply;
  /** The commented words, shown once, under the message that started the thread. */
  quote?: string;
  editing?: boolean;
  onSaveEdit?: (body: string) => void;
  onCancelEdit?: () => void;
  /** Omitted when this message is not the reader's to rewrite. */
  onStartEdit?: () => void;
  onDelete?: () => void;
  /** True when the card is showing this message's menu in its own corner instead. */
  hostsOwnMenu?: boolean;
}) {
  const hasMenu =
    hostsOwnMenu !== true &&
    editing !== true &&
    (onStartEdit !== undefined || onDelete !== undefined);

  return (
    <div className="group/message relative">
      {/* Per message, not per thread. A reply is someone's own sentence, and fixing
          or withdrawing it should not mean reaching for a control that acts on the
          whole conversation. Appears on approach, like the resolve control.

          Hidden when the card has taken it into its corner — see `hostsOwnMenu`. */}
      {hasMenu ? (
        <div className="absolute right-0 top-0 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover/message:opacity-100">
          <CommentMessageMenu
            reply={reply}
            {...(onStartEdit !== undefined && { onStartEdit })}
            {...(onDelete !== undefined && { onDelete })}
          />
        </div>
      ) : null}
      <CommentByline reply={reply}>
        {quote !== undefined && quote !== '' ? (
          // A quiet rule, not the amber of the highlight. In the margin the card is
          // already beside the words it quotes, so a second loud marker competes with
          // the one in the prose instead of pointing at it.
          <p className="my-0.5 border-l-2 border-border pl-2 text-sm leading-5 text-muted-foreground">
            {quote}
          </p>
        ) : null}
        {editing === true &&
        onSaveEdit !== undefined &&
        onCancelEdit !== undefined ? (
          <CommentMessageEditor
            initialBody={reply.body}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm leading-5">
            {reply.body}
          </p>
        )}
      </CommentByline>
    </div>
  );
}
